// Servicio para gestión de tickets integrado con n8n webhooks

// Interfaces TypeScript
export interface RawTicketData {
  id?: string;
  ticketId?: string;
  ticket_id?: string;
  name?: string;
  title?: string;
  subject?: string;
  problema?: string;
  description?: string;
  text_content?: string;
  details?: string;
  status?: any;
  statusRaw?: string;
  statusType?: 'closed' | 'custom' | 'open' | null;
  statusId?: string | number;
  priority?: any;
  priorityRaw?: string;
  priorityId?: string | number;
  prioridad?: string;
  source?: string;
  origen?: string;
  escalated?: boolean;
  es_escalado?: boolean;
  // Campos de fecha - formato original
  date_created?: string;
  createdAt?: string;
  created_at?: string;
  fecha_creacion?: string;
  timestamp?: string;
  date_updated?: string;
  updatedAt?: string;
  updated_at?: string;
  ultima_actualizacion?: string;
  // Nuevos campos de fecha con timestamps en milisegundos
  createdAtMs?: number;
  updatedAtMs?: number;
  closedAt?: string | null;
  closedAtMs?: number | null;
  // Campos de edad del ticket
  ageHours?: number;
  ageDays?: number;
  // Campos de asignación
  assignedTo?: string;
  assigned_to?: string;
  assignedToEmail?: string;
  assigned_to_email?: string;
  assignees?: any[];
  userEmail?: string;
  user_email?: string;
  email?: string;
  requesterName?: string;
  requester_name?: string;
  resolved?: boolean;
  solution?: string;
  solucion?: string;
  tags?: string[];
  attachments?: any[];
  adjuntos?: any[];
  conversationHistory?: any[];
  historial_conversacion?: any[];
  custom_fields?: any[];
  checklists?: any[];
  watchers?: any[];
  // Campos de progreso
  progressPercent?: number;
  progressSource?: string;
  progressBreakdown?: {
    resolvedItems?: number | null;
    totalItems?: number | null;
    checklists?: number;
    [key: string]: any;
  };
  // Campos de ubicación en ClickUp
  location?: {
    listId?: string | null;
    listName?: string;
    projectId?: string | null;
    projectName?: string;
    folderId?: string | null;
    folderName?: string;
    teamId?: string | null;
    spaceId?: string | null;
    [key: string]: any;
  };
  // URLs y ClickUp específicos
  url?: string;
  clickupUrl?: string;
  clickup_url?: string;
  clickup?: {
    custom_id?: string | null;
    orderindex?: string | null;
    due_date?: string | null;
    start_date?: string | null;
    time_estimate?: number | null;
    points?: number | null;
    custom_fields?: any[];
    [key: string]: any;
  };
  estado?: string;
  [key: string]: any;
}

export interface HistoryEntry {
  id: string;
  type: 'created' | 'status_change' | 'assignment' | 'priority_change' | 'comment' | 'attachment' | 'escalation' | 'resolution' | 'update' | 'custom';
  action: string;
  description: string;
  author: string;
  authorAvatar?: string;
  timestamp: string;
  data?: any; // Datos adicionales específicos del evento
  fromValue?: string; // Valor anterior (para cambios)
  toValue?: string; // Valor nuevo (para cambios)
  metadata?: {
    [key: string]: any;
  };
}

export interface NormalizedTicket {
  id: string;
  title: string;
  description: string;
  status: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado';
  priority: 'alta' | 'media' | 'baja';
  source: string;
  createdAt: string;
  updatedAt: string;
  assignedTo: string;
  assignedToAvatar?: string | null;
  assignedToData?: any | null;
  assignedToEmail: string;
  userEmail: string;
  requesterName: string;
  escalated: boolean;
  resolved: boolean;
  solution: string;
  tags: string[];
  attachments: any[];
  conversationHistory: any[];
  custom_fields: any[];
  checklists: any[];
  assignees: any[];
  watchers: any[];
  progressPercent?: number;
  progressSource?: string;
  clickupUrl: string;
  clickup: any;
  category?: string; // Nueva: categoría extraída del webhook
  timeEstimate?: string; // Nueva: tiempo estimado formateado
  timeEstimateMs?: number; // Nueva: tiempo estimado en milisegundos
  history?: HistoryEntry[]; // Nueva: historial normalizado del ticket
  _raw: RawTicketData;
}

export interface WebhookResponse {
  message?: string;
  success?: boolean;
  userEmail?: string;
  tickets?: RawTicketData[];
  data?: RawTicketData[];
  results?: RawTicketData[];
  [key: string]: any;
}

export interface DiagnosticIssue {
  ticketId: string;
  issue: string;
  originalStatus?: string;
  currentStatus?: string;
  expectedStatus?: string;
  progressPercent?: number;
  note?: string;
}

export interface DiagnosticData {
  totalTickets: number;
  assignedToValues: Map<string, number>;
  uniqueAssignees: Set<string>;
  potentialIssues: string[];
  ticketDetails: TicketDetail[];
}

export interface TicketDetail {
  index: number;
  id: string;
  assignedTo: string;
  assignedTo_type: string;
  assignedTo_length: number;
  assignedTo_reference: any;
  hasAssignees: boolean;
  originalAssignees?: string[];
  _rawAssignedTo?: any;
}

export interface ValidationReport {
  totalTickets: number;
  issuesFound: DiagnosticIssue[];
  suspicious: DiagnosticIssue[];
  recommendations: string[];
}

export interface TicketDiagnosis {
  found: boolean;
  error?: string;
  ticketId?: string;
  currentStatus?: string;
  currentStatusDisplay?: string;
  rawTicketData?: {
    originalStatus?: any;
    statusRaw?: string;
    statusType?: string | null;
    originalStatusObject?: any;
    progressPercent?: number;
    progressBreakdown?: any;
    resolved?: boolean;
    assignees?: any[];
    custom_fields?: any[];
    location?: any;
  };
  statusAnalysis?: {
    normalizedOriginal?: string;
    smartStatus?: string;
    reasoning?: string;
  };
  potentialIssues: string[];
  recommendations: string[];
}

export interface TicketStats {
  total: number;
  abierto: number;
  en_progreso: number;
  resuelto: number;
  cerrado: number;
  alta: number;
  media: number;
  baja: number;
  escalados: number;
  eliasAI: number;
}

const TicketService = {
  // Constantes
  WEBHOOK_BASE_URL: 'https://inmobiliaria-ecomac.app.n8n.cloud/webhook',
  TICKETS_ENDPOINT: 'https://inmobiliaria-ecomac.app.n8n.cloud/webhook/b330d7ad-fb9e-4b1b-9a3b-800f5ba239a1',
  
  /**
   * Obtener todos los tickets del usuario (alias para fetchTickets)
   * @param userEmail - Email del usuario logueado
   * @returns Lista de tickets
   */
  async getUserTickets(userEmail: string): Promise<NormalizedTicket[]> {
    return this.fetchTickets(userEmail);
  },

  /**
   * Obtener todos los tickets del usuario
   * @param userEmail - Email del usuario logueado
   * @returns Lista de tickets
   */
  async fetchTickets(userEmail: string): Promise<NormalizedTicket[]> {
    try {
      // Validar que se proporcione email de usuario
      if (!userEmail) {
        throw new Error('Email de usuario requerido para obtener tickets');
      }
      const response = await fetch(this.TICKETS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: userEmail,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status} - ${response.statusText}`);
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Si no es JSON, intentamos convertir la respuesta a texto
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch (e) {
          throw new Error('Respuesta inválida del webhook');
        }
      }
      // Manejar respuesta asíncrona de n8n - webhook funciona pero es asíncrono
      if (data && data.message === 'Workflow was started') {
        // Configuración optimizada basada en el comportamiento real del webhook
        const maxRetries = 8; // Más reintentos
        const delays = [3000, 5000, 7000, 10000, 12000, 15000, 18000, 20000]; // Delays progresivos
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          const delay = delays[attempt - 1];
          await new Promise(resolve => setTimeout(resolve, delay));
          
          try {
            const retryResponse = await fetch(this.TICKETS_ENDPOINT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                userEmail: userEmail,
                timestamp: new Date().toISOString(),
                retry: attempt,
                requestType: 'getData' // Solicitar datos específicamente
              })
            });
            
            if (retryResponse.ok) {
              const responseText = await retryResponse.text();
              try {
                const retryData = JSON.parse(responseText);
                // Verificar si tenemos datos reales
                if (retryData && retryData.message !== 'Workflow was started') {
                  // Verificar si tiene la estructura de datos que esperamos
                  if (Array.isArray(retryData) && retryData[0] && retryData[0].tickets) {
                    data = retryData;
                    break;
                  } else if (retryData.tickets && Array.isArray(retryData.tickets)) {
                    data = retryData;
                    break;
                  } else if (retryData.success && retryData.userEmail) {
                    data = [retryData]; // Envolver en array para mantener consistencia
                    break;
                  }
                }
                
                // Si llegamos al último intento y sigue sin datos
                if (attempt === maxRetries) {
                  return [];
                }
                
              } catch (parseError) {
                // Error silencioso al parsear - continuar con siguientes intentos
              }
            } else {
              // Reintento falló - continuar con siguientes intentos
            }
            
          } catch (retryError) {
            if (attempt === maxRetries) {
              throw new Error('No se pudieron obtener tickets después de múltiples intentos');
            }
          }
        }
      }

      // Procesar y normalizar la respuesta
      const tickets = this.normalizeTicketsData(data);
      return tickets;

    } catch (error) {
      throw error;
    }
  },

  /**
   * Obtener un ticket específico por ID
   * @param id - ID del ticket
   * @returns Ticket específico
   */
  async getTicket(id: string | number): Promise<NormalizedTicket> {
    if (!id) {
      throw new Error('ID de ticket requerido');
    }
    
    // TODO: Implementar obtención real de detalles del ticket desde webhook
    throw new Error(`Detalles del ticket ${id} no disponibles - función no implementada`);
  },

  /**
   * Crear un nuevo ticket
   * @param data - Datos del nuevo ticket
   * @returns Ticket creado
   */
  createTicket(data: Partial<RawTicketData>): Promise<any> {
    // Por ahora simulamos la creación
    return Promise.resolve({ ...data, id: Date.now() });
  },
  
  /**
   * Actualizar un ticket existente
   * @param id - ID del ticket a actualizar
   * @param ticketData - Datos actualizados del ticket
   * @returns Ticket actualizado
   */
  async updateTicket(id: string | number, ticketData: Partial<RawTicketData>): Promise<any> {
    // Simular delay de red
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // En un entorno real, esto sería una llamada HTTP PUT/PATCH
    // Por ahora simulamos la respuesta exitosa
    const updatedTicket = {
      ...ticketData,
      id: typeof id === 'string' ? parseInt(id) : id,
      updatedAt: new Date().toISOString()
    };
    return Promise.resolve(updatedTicket);
  },
  
  /**
   * Obtener detalles completos de un ticket por ID
   * @param id - ID del ticket
   * @returns Detalles del ticket
   */
  async fetchTicketDetails(id: string | number): Promise<NormalizedTicket> {
    if (!id) {
      throw new Error('ID de ticket requerido para obtener detalles');
    }
    
    // TODO: Implementar obtención real de detalles del ticket desde webhook
    throw new Error(`Detalles del ticket ${id} no disponibles - función no implementada`);
  },

  /**
   * Convierte timestamp de ClickUp (microsegundos) a ISO string
   * @param timestamp - Timestamp de ClickUp en microsegundos o string
   * @returns String ISO de fecha o null si no es válido
   */
  convertClickUpTimestamp(timestamp: string | number | null | undefined): string | null {
    if (!timestamp) return null;
    
    try {
      let numericTimestamp: number;
      
      // Convertir a número si viene como string
      if (typeof timestamp === 'string') {
        numericTimestamp = parseInt(timestamp);
      } else {
        numericTimestamp = timestamp;
      }
      
      console.log('🗓️ ClickUp timestamp conversion:', {
        original: timestamp,
        type: typeof timestamp,
        numeric: numericTimestamp,
        digits: numericTimestamp.toString().length
      });
      
      // Verificar si es un timestamp válido
      if (isNaN(numericTimestamp) || numericTimestamp <= 0) {
        console.warn('Timestamp inválido recibido:', timestamp);
        return null;
      }
      
      // ClickUp usa microsegundos (13 dígitos), JavaScript usa milisegundos (10 dígitos)
      // Si el timestamp tiene más de 10 dígitos, convertir de microsegundos a milisegundos
      const originalTimestamp = numericTimestamp;
      if (numericTimestamp.toString().length > 10) {
        numericTimestamp = Math.floor(numericTimestamp / 1000);
        console.log('🔄 Converting microseconds to milliseconds:', originalTimestamp, '->', numericTimestamp);
      }
      
      const date = new Date(numericTimestamp);
      
      // Verificar que la fecha resultante sea válida
      if (isNaN(date.getTime())) {
        console.warn('Fecha inválida después de conversión:', timestamp, '->', numericTimestamp);
        return null;
      }
      
      // Verificar que la fecha no sea demasiado en el futuro (más de 1 año)
      const now = new Date();
      const oneYearFromNow = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
      
      console.log('📅 Date conversion result:', {
        originalTimestamp: timestamp,
        finalTimestamp: numericTimestamp,
        resultDate: date.toISOString(),
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        isFuture: date > oneYearFromNow
      });
      
      if (date > oneYearFromNow) {
        console.warn('⚠️ Fecha sospechosa muy futura detectada:', date.toISOString(), 'from timestamp:', timestamp);
        // Aún devolver la fecha pero con warning
      }
      
      return date.toISOString();
      
    } catch (error) {
      console.error('Error convirtiendo timestamp de ClickUp:', timestamp, error);
      return null;
    }
  },

  /**
   * Normaliza los datos de tickets recibidos del webhook
   * @param data - Datos raw del webhook
   * @returns Array normalizado de tickets
   */
  normalizeTicketsData(data: any): NormalizedTicket[] {
    try {
      // Manejar respuesta de n8n que viene como array con objeto
      if (Array.isArray(data) && data.length > 0 && data[0].tickets) {
        return data[0].tickets.map((ticket: RawTicketData) => this.normalizeTicket(ticket));
      }
      
      // Si viene un array de tickets directamente
      if (Array.isArray(data) && data.length > 0 && (data[0].id || data[0].ticketId)) {
        return data.map((ticket: RawTicketData) => this.normalizeTicket(ticket));
      }

      // Si viene envuelto en un objeto con propiedad que contenga los tickets
      if (data.tickets && Array.isArray(data.tickets)) {
        return data.tickets.map((ticket: RawTicketData) => this.normalizeTicket(ticket));
      }

      // Si viene en otras propiedades comunes
      if (data.data && Array.isArray(data.data)) {
        return data.data.map((ticket: RawTicketData) => this.normalizeTicket(ticket));
      }

      if (data.results && Array.isArray(data.results)) {
        return data.results.map((ticket: RawTicketData) => this.normalizeTicket(ticket));
      }

      // Si viene un solo objeto ticket
      if (data.id || data.ticketId) {
        return [this.normalizeTicket(data)];
      }

      // Si no hay tickets, retornar array vacío
      return [];

    } catch (error) {
      return [];
    }
  },

  /**
   * Normaliza un ticket individual a formato estándar
   * @param ticket - Ticket raw del webhook
   * @returns Ticket normalizado
   */
  normalizeTicket(ticket: RawTicketData): NormalizedTicket {
    
    // DEBUG: Verificar qué campos de fecha vienen del webhook
    console.log('📅 [DEBUG] Campos de fecha del webhook para ticket:', {
      id: ticket.id || 'unknown',
      title: ticket.title || ticket.name || 'sin título',
      createdAt: ticket.createdAt,
      createdAtMs: ticket.createdAtMs,
      updatedAt: ticket.updatedAt, 
      updatedAtMs: ticket.updatedAtMs,
      closedAt: ticket.closedAt,
      closedAtMs: ticket.closedAtMs,
      ageHours: ticket.ageHours,
      ageDays: ticket.ageDays,
      location: ticket.location
    });
    
    // Extraer assignees de ClickUp para determinar quién está asignado y su avatar
    let assignedTo = 'Elias AI'; // Valor por defecto
    let assignedToAvatar = null; // Avatar del asignado
    let assignedToData = null; // Datos completos del asignado
    
    // SIEMPRE extraer avatar de assignees si está disponible
    if (ticket.assignees && Array.isArray(ticket.assignees) && ticket.assignees.length > 0) {
      const firstAssignee = ticket.assignees[0];
      
      
      // Extraer avatar en múltiples campos posibles
      assignedToAvatar = firstAssignee.profilePicture || 
                        firstAssignee.profile_picture || 
                        firstAssignee.avatar || 
                        firstAssignee.profileImage ||
                        firstAssignee.profile_image ||
                        firstAssignee.image ||
                        firstAssignee.photo ||
                        firstAssignee.pic ||
                        firstAssignee.picture ||
                        null;
      
      assignedToData = firstAssignee;
    }
    
    // Determinar assignedTo con prioridades
    // PRIORIDAD 1: Si ya viene assignedTo del webhook, usarlo
    if (ticket.assignedTo && typeof ticket.assignedTo === 'string' && ticket.assignedTo.trim() !== '') {
      assignedTo = String(ticket.assignedTo).trim();
    }
    // PRIORIDAD 2: Extraer de assignees de ClickUp si está disponible
    else if (ticket.assignees && Array.isArray(ticket.assignees) && ticket.assignees.length > 0) {
      const firstAssignee = ticket.assignees[0];
      assignedTo = String(firstAssignee.username || firstAssignee.email || firstAssignee.name || 'Elias AI').trim();
    }
    // PRIORIDAD 3: Buscar en otros campos comunes
    else if (ticket.assigned_to && typeof ticket.assigned_to === 'string') {
      assignedTo = String(ticket.assigned_to).trim();
    }
    
    // CRÍTICO: Determinar el estado real basado en progreso y estado original
    // NUEVO: Usar statusRaw si está disponible, ya que es más preciso
    const originalStatus = ticket.statusRaw || ticket.status?.status || ticket.status || ticket.estado || 'abierto';
    const statusType = ticket.statusType || null; // 'closed', 'custom', 'open'
    const progressPercent = ticket.progressPercent || 0;
    const smartStatus = this.determineSmartStatus(originalStatus, progressPercent, ticket.resolved, statusType);
    
    
    // Manejar fechas con prioridad a los nuevos campos en milisegundos
    let createdAtDate = new Date().toISOString(); // Fallback por defecto
    let updatedAtDate = new Date().toISOString(); // Fallback por defecto
    
    // PRIORIDAD 1: Usar createdAtMs si está disponible (más confiable)
    if (ticket.createdAtMs && typeof ticket.createdAtMs === 'number') {
      try {
        createdAtDate = new Date(ticket.createdAtMs).toISOString();
        console.log('✅ Using createdAtMs:', ticket.createdAtMs, '→', createdAtDate);
      } catch (error) {
        console.warn('❌ Error converting createdAtMs:', ticket.createdAtMs, error);
      }
    }
    // PRIORIDAD 2: Usar campos ISO string directamente
    else if (ticket.createdAt && typeof ticket.createdAt === 'string') {
      createdAtDate = ticket.createdAt;
      console.log('✅ Using createdAt ISO string:', createdAtDate);
    }
    // PRIORIDAD 3: Intentar convertir timestamps de ClickUp
    else if (ticket.date_created) {
      const converted = this.convertClickUpTimestamp(ticket.date_created);
      if (converted) {
        createdAtDate = converted;
        console.log('✅ Using converted date_created:', converted);
      }
    }
    
    // Similar para updatedAt
    if (ticket.updatedAtMs && typeof ticket.updatedAtMs === 'number') {
      try {
        updatedAtDate = new Date(ticket.updatedAtMs).toISOString();
        console.log('✅ Using updatedAtMs:', ticket.updatedAtMs, '→', updatedAtDate);
      } catch (error) {
        console.warn('❌ Error converting updatedAtMs:', ticket.updatedAtMs, error);
      }
    }
    else if (ticket.updatedAt && typeof ticket.updatedAt === 'string') {
      updatedAtDate = ticket.updatedAt;
      console.log('✅ Using updatedAt ISO string:', updatedAtDate);
    }
    else if (ticket.date_updated) {
      const converted = this.convertClickUpTimestamp(ticket.date_updated);
      if (converted) {
        updatedAtDate = converted;
        console.log('✅ Using converted date_updated:', converted);
      }
    }
    
    // Generar ID único si no existe
    let ticketId: string;
    if (ticket.id || ticket.ticketId || ticket.ticket_id) {
      ticketId = String(ticket.id || ticket.ticketId || ticket.ticket_id);
    } else {
      // Generar ID basado en información del ticket para consistencia
      const baseInfo = {
        title: ticket.title || 'untitled',
        user: ticket.userEmail || 'unknown',
        created: ticket.createdAtMs || ticket.createdAt || Date.now()
      };
      
      // Crear hash simple basado en la información del ticket
      const hashInput = `${baseInfo.title}-${baseInfo.user}-${baseInfo.created}`;
      const hash = btoa(hashInput).replace(/[^a-zA-Z0-9]/g, '').substring(0, 8);
      ticketId = `ticket-${hash}`;
      
      console.log('🆔 Generated ticket ID:', ticketId, 'from:', baseInfo);
    }
    
    // Extraer categoría y tiempo estimado
    const timeEstimateData = this.extractTimeEstimate(ticket);
    const category = this.extractTicketCategory(ticket);
    
    // Construir historial del ticket
    const history = this.buildTicketHistory(ticket, ticketId, createdAtDate, updatedAtDate);
    
    const normalizedTicket = {
      id: ticketId,
      title: ticket.title || ticket.name || ticket.subject || ticket.problema || ticket.description || 'Sin título',
      description: ticket.description || ticket.text_content || ticket.details || ticket.problema || ticket.title || '',
      status: smartStatus,
      priority: this.normalizePriority(ticket.priority?.priority || ticket.priority || ticket.prioridad || 'media'),
      source: ticket.source || ticket.origen || (ticket.escalated ? 'escalado' : 'elias-ai'),
      createdAt: createdAtDate,
      updatedAt: updatedAtDate,
      // CRÍTICO: Crear nueva string para evitar referencias compartidas
      assignedTo: String(assignedTo),
      assignedToAvatar: assignedToAvatar, // Avatar del usuario asignado
      assignedToData: assignedToData ? JSON.parse(JSON.stringify(assignedToData)) : null, // Datos completos del asignado
      assignedToEmail: ticket.assignedToEmail || ticket.assigned_to_email || '',
      userEmail: ticket.userEmail || ticket.user_email || ticket.email || '',
      requesterName: ticket.requesterName || ticket.requester_name || ticket.userEmail || '',
      escalated: ticket.escalated || ticket.es_escalado || false,
      resolved: this.determineResolvedStatus(smartStatus, progressPercent, ticket.resolved),
      solution: ticket.solution || ticket.solucion || '',
      // CRÍTICO: Crear nuevas arrays para evitar referencias compartidas
      tags: Array.isArray(ticket.tags) ? [...ticket.tags] : [],
      attachments: Array.isArray(ticket.attachments) ? [...ticket.attachments] : (Array.isArray(ticket.adjuntos) ? [...ticket.adjuntos] : []),
      conversationHistory: Array.isArray(ticket.conversationHistory) ? [...ticket.conversationHistory] : (Array.isArray(ticket.historial_conversacion) ? [...ticket.historial_conversacion] : []),
      // Preservar campos originales de ClickUp con copias profundas
      custom_fields: Array.isArray(ticket.custom_fields) ? JSON.parse(JSON.stringify(ticket.custom_fields)) : [],
      checklists: Array.isArray(ticket.checklists) ? JSON.parse(JSON.stringify(ticket.checklists)) : [],
      assignees: Array.isArray(ticket.assignees) ? JSON.parse(JSON.stringify(ticket.assignees)) : [],
      watchers: Array.isArray(ticket.watchers) ? JSON.parse(JSON.stringify(ticket.watchers)) : [],
      // Preservar progressPercent del webhook
      progressPercent: ticket.progressPercent,
      progressSource: ticket.progressSource,
      // Campos específicos de ClickUp
      clickupUrl: ticket.url || ticket.clickupUrl || ticket.clickup_url || '',
      clickup: ticket.clickup ? JSON.parse(JSON.stringify(ticket.clickup)) : {},
      // NUEVOS: Categoría y tiempo estimado extraídos
      category: category,
      timeEstimate: timeEstimateData.formatted || undefined,
      timeEstimateMs: timeEstimateData.milliseconds || undefined,
      // NUEVO: Historial del ticket
      history: history,
      _raw: JSON.parse(JSON.stringify(ticket))
    };
    return normalizedTicket;
  },

  /**
   * Construye el historial del ticket basado en los datos disponibles
   * @param {Object} ticket - Ticket raw del webhook
   * @param {string} ticketId - ID del ticket normalizado
   * @param {string} createdAt - Fecha de creación
   * @param {string} updatedAt - Fecha de actualización
   * @returns {Array} Array de eventos del historial
   */
  buildTicketHistory(ticket: RawTicketData, ticketId: string, createdAt: string, updatedAt: string): HistoryEntry[] {
    const history: HistoryEntry[] = [];
    
    // EVENTO 1: Creación del ticket (siempre presente)
    const createdEntry: HistoryEntry = {
      id: `${ticketId}-created-${Date.now()}`,
      type: 'created',
      action: 'Ticket creado',
      description: `Nuevo ticket reportado: "${ticket.title || ticket.name || 'Sin título'}"`+
                  (ticket.source ? ` desde ${ticket.source}` : ''),
      author: ticket.requesterName || ticket.requester_name || ticket.userEmail || 'Sistema',
      timestamp: createdAt,
      data: {
        initialStatus: ticket.status,
        initialPriority: ticket.priority,
        source: ticket.source || 'elias-ai'
      },
      metadata: {
        system: true,
        automatic: true
      }
    };
    history.push(createdEntry);
    
    // EVENTO 2: Asignación (si hay assignees o assignedTo)
    if ((ticket.assignees && ticket.assignees.length > 0) || ticket.assignedTo) {
      const assignee = ticket.assignees?.[0] || { name: ticket.assignedTo };
      const assigneeName = assignee.username || assignee.name || assignee.email || ticket.assignedTo || 'Usuario desconocido';
      
      const assignmentEntry: HistoryEntry = {
        id: `${ticketId}-assigned-${Date.now()}`,
        type: 'assignment',
        action: 'Ticket asignado',
        description: `Asignado a ${assigneeName}`,
        author: 'Sistema',
        authorAvatar: assignee.profilePicture || assignee.profile_picture || assignee.avatar,
        timestamp: createdAt, // Por defecto, la asignación ocurrió en la creación
        toValue: assigneeName,
        data: {
          assigneeData: assignee,
          assigneeId: assignee.id
        },
        metadata: {
          system: true,
          automatic: true
        }
      };
      history.push(assignmentEntry);
    }
    
    // EVENTO 3: Cambio de estado (si el estado actual no es 'abierto')
    const currentStatus = this.normalizeStatus(ticket.statusRaw || ticket.status?.status || ticket.status);
    if (currentStatus !== 'abierto') {
      let statusDescription = '';
      switch (currentStatus) {
        case 'en_progreso':
          statusDescription = 'Ticket movido a "En Progreso"';
          break;
        case 'resuelto':
          statusDescription = 'Ticket marcado como "Resuelto"';
          break;
        case 'cerrado':
          statusDescription = 'Ticket cerrado';
          break;
        default:
          statusDescription = `Estado cambiado a "${this.formatStatusText(currentStatus)}"`;
      }
      
      const statusEntry: HistoryEntry = {
        id: `${ticketId}-status-${Date.now()}`,
        type: 'status_change',
        action: 'Estado actualizado',
        description: statusDescription,
        author: ticket.assignedTo || 'Sistema',
        timestamp: updatedAt,
        fromValue: 'abierto',
        toValue: currentStatus,
        data: {
          originalStatus: ticket.statusRaw || ticket.status,
          progressPercent: ticket.progressPercent,
          statusType: ticket.statusType
        },
        metadata: {
          automatic: (ticket.progressPercent ?? 0) >= 100 // Si fue por progreso completo
        }
      };
      history.push(statusEntry);
    }
    
    // EVENTO 4: Escalación (si está escalado)
    if (ticket.escalated || ticket.es_escalado) {
      const escalationEntry: HistoryEntry = {
        id: `${ticketId}-escalated-${Date.now()}`,
        type: 'escalation',
        action: 'Ticket escalado',
        description: 'Ticket escalado para atención especializada',
        author: 'Sistema',
        timestamp: updatedAt, // Asumimos que la escalación ocurrió en la actualización
        data: {
          reason: 'Escalación automática',
          escalatedTo: 'Equipo técnico'
        },
        metadata: {
          system: true,
          priority: 'high'
        }
      };
      history.push(escalationEntry);
    }
    
    // EVENTO 5: Comentarios desde conversationHistory
    if (ticket.conversationHistory && ticket.conversationHistory.length > 0) {
      ticket.conversationHistory.forEach((comment: any, index: number) => {
        if (comment && (comment.content || comment.message || comment.text)) {
          const commentEntry: HistoryEntry = {
            id: `${ticketId}-comment-${index}-${Date.now()}`,
            type: 'comment',
            action: 'Comentario añadido',
            description: comment.content || comment.message || comment.text,
            author: comment.author || comment.user || comment.from || 'Usuario',
            authorAvatar: comment.avatar || comment.profilePicture,
            timestamp: comment.timestamp || comment.date || comment.createdAt || updatedAt,
            data: {
              commentId: comment.id,
              originalComment: comment
            }
          };
          history.push(commentEntry);
        }
      });
    }
    
    // EVENTO 6: Adjuntos
    if ((ticket.attachments && ticket.attachments.length > 0) || (ticket.adjuntos && ticket.adjuntos.length > 0)) {
      const attachments = ticket.attachments || ticket.adjuntos || [];
      attachments.forEach((attachment: any, index: number) => {
        if (attachment) {
          const attachmentEntry: HistoryEntry = {
            id: `${ticketId}-attachment-${index}-${Date.now()}`,
            type: 'attachment',
            action: 'Archivo adjuntado',
            description: `Archivo adjuntado: ${attachment.name || attachment.filename || 'archivo.ext'}`,
            author: attachment.uploadedBy || ticket.requesterName || 'Usuario',
            timestamp: attachment.uploadedAt || attachment.createdAt || createdAt,
            data: {
              filename: attachment.name || attachment.filename,
              size: attachment.size,
              type: attachment.type || attachment.mimeType,
              url: attachment.url || attachment.link
            }
          };
          history.push(attachmentEntry);
        }
      });
    }
    
    // EVENTO 7: Resolución (si tiene solución)
    if (ticket.solution || ticket.solucion) {
      const resolutionEntry: HistoryEntry = {
        id: `${ticketId}-resolved-${Date.now()}`,
        type: 'resolution',
        action: 'Solución proporcionada',
        description: ticket.solution || ticket.solucion || 'Solución aplicada',
        author: ticket.assignedTo || 'Técnico',
        timestamp: updatedAt,
        data: {
          solution: ticket.solution || ticket.solucion,
          resolvedBy: ticket.assignedTo
        },
        metadata: {
          resolution: true
        }
      };
      history.push(resolutionEntry);
    }
    
    // EVENTO 8: Actualizaciones generales (si hay diferencia significativa entre creación y actualización)
    const createdTime = new Date(createdAt).getTime();
    const updatedTime = new Date(updatedAt).getTime();
    const timeDifference = updatedTime - createdTime;
    const minimumUpdateTime = 60 * 1000; // 1 minuto
    
    if (timeDifference > minimumUpdateTime && currentStatus === 'abierto') {
      const updateEntry: HistoryEntry = {
        id: `${ticketId}-updated-${Date.now()}`,
        type: 'update',
        action: 'Ticket actualizado',
        description: 'Información del ticket actualizada',
        author: ticket.assignedTo || 'Sistema',
        timestamp: updatedAt,
        data: {
          fields: ['general'],
          timeSinceCreation: this.formatDurationFromMs(timeDifference)
        },
        metadata: {
          system: true,
          minor: true
        }
      };
      history.push(updateEntry);
    }
    
    // Ordenar historial por timestamp
    history.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    
    return history;
  },

  /**
   * Determina el estado inteligente basado en progreso y estado original
   * @param {string} originalStatus - Estado original del webhook (statusRaw preferido)
   * @param {number} progressPercent - Porcentaje de progreso (0-100)
   * @param {boolean} resolved - Estado resolved del webhook
   * @param {string} statusType - Tipo de estado ('closed', 'custom', 'open')
   * @returns {string} Estado normalizado e inteligente
   */
  determineSmartStatus(originalStatus: any, progressPercent: number = 0, resolved: boolean = false, statusType: string | null = null): 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado' {
    const normalizedOriginal = this.normalizeStatus(originalStatus);
    
    // PRIORIDAD 1: Usar statusType de ClickUp para decisiones más precisas
    if (statusType === 'closed') {
      // Si ClickUp dice que está cerrado, respetarlo
      return normalizedOriginal === 'cerrado' ? 'cerrado' : 'resuelto';
    }
    
    // PRIORIDAD 2: Si el estado original de ClickUp ya es definitivo (cerrado/resuelto), respetarlo
    // CRÍTICO: Esto debe ir ANTES del flag 'resolved' para respetar estados específicos como 'listo'
    if (['cerrado', 'resuelto'].includes(normalizedOriginal)) {
      return normalizedOriginal;
    }
    
    // PRIORIDAD 3: Si está explícitamente marcado como resolved, es resuelto
    if (resolved === true) {
      return 'resuelto';
    }
    
    // PRIORIDAD 4: Si tiene 100% de progreso pero no está cerrado en ClickUp, es resuelto (listo para cerrar)
    if (progressPercent >= 100) {
      return 'resuelto';
    }
    
    // PRIORIDAD 5: Si tiene progreso alto (90-99%), probablemente está casi listo
    if (progressPercent >= 90 && progressPercent < 100) {
      return 'en_progreso';
    }
    
    // PRIORIDAD 6: Si tiene progreso medio-alto (50-89%), está en progreso
    if (progressPercent >= 50 && progressPercent < 90) {
      return 'en_progreso';
    }
    
    // PRIORIDAD 7: Si tiene progreso bajo (1-49%), evaluar si ponerlo en progreso
    if (progressPercent > 0 && progressPercent < 50) {
      return normalizedOriginal === 'abierto' ? 'en_progreso' : normalizedOriginal;
    }
    
    // PRIORIDAD 8: Si no tiene progreso (0%), usar el estado original normalizado
    return normalizedOriginal;
  },

  /**
   * Determina el estado resolved basado en el status y progreso
   * @param {string} smartStatus - Estado inteligente determinado
   * @param {number} progressPercent - Porcentaje de progreso
   * @param {boolean} originalResolved - Estado resolved original
   * @returns {boolean} Si el ticket está resuelto
   */
  determineResolvedStatus(smartStatus: string, progressPercent: number = 0, originalResolved: boolean = false): boolean {
    // Si el smart status dice que está resuelto, entonces está resuelto
    if (smartStatus === 'resuelto' || smartStatus === 'cerrado') {
      return true;
    }
    
    // Si tiene 100% de progreso, está resuelto
    if (progressPercent >= 100) {
      return true;
    }
    
    // Sino, usar el estado original
    return originalResolved;
  },

  /**
   * Genera log de reasoning para debugging
   * @param {string} originalStatus - Estado original
   * @param {number} progressPercent - Progreso
   * @param {boolean} resolved - Resolved original
   * @returns {string} Razón del mapeo
   */
  getStatusReasoningLog(originalStatus: any, progressPercent: number, resolved: boolean): string {
    const normalized = this.normalizeStatus(originalStatus);
    
    if (resolved === true) {
      return `Marked as resolved explicitly (resolved=${resolved})`;
    }
    
    // CASO ESPECIAL: Detectar tickets que deberían estar cerrados
    if (progressPercent >= 100 && originalStatus?.toLowerCase() === 'abierto') {
      return `⚠️  POSSIBLE ISSUE: 100% progress but originalStatus="${originalStatus}" - this might be "LISTO" in ClickUp`;
    }
    
    if (['cerrado', 'resuelto'].includes(normalized)) {
      return `Using definitive ClickUp status "${originalStatus}" (normalized: ${normalized})`;
    }
    
    if (progressPercent >= 100) {
      return `100% progress overrides original status "${originalStatus}"`;
    }
    if (progressPercent >= 90) {
      return `High progress (${progressPercent}%) suggests near completion`;
    }
    if (progressPercent >= 50) {
      return `Medium-high progress (${progressPercent}%) suggests in_progress`;
    }
    if (progressPercent > 0) {
      return `Low progress (${progressPercent}%) with original status "${originalStatus}"`;
    }
    return `No progress data, using original status "${originalStatus}"`;
  },

  /**
   * Normaliza el estado del ticket (función básica)
   * @param {string} status - Estado raw
   * @returns {string} Estado normalizado
   */
  normalizeStatus(status: any): 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado' {
    if (!status || typeof status !== 'string') return 'abierto';

    const statusMap = {
      // Estados abiertos/nuevos
      'abierto': 'abierto' as const,
      'open': 'abierto' as const,
      'pending': 'abierto' as const,
      'nuevo': 'abierto' as const,
      'to do': 'abierto' as const,
      'todo': 'abierto' as const,
      'backlog': 'abierto' as const,
      'pendientes': 'abierto' as const, // NUEVO del webhook
      'evaluación': 'abierto' as const, // NUEVO - para ticket #86b69rveg
      'evaluacion': 'abierto' as const, // Versión sin tilde
      // Estados en progreso
      'en_progreso': 'en_progreso' as const,
      'en progreso': 'en_progreso' as const,
      'in_progress': 'en_progreso' as const,
      'in progress': 'en_progreso' as const,
      'working': 'en_progreso' as const,
      'asignado': 'en_progreso' as const,
      'doing': 'en_progreso' as const,
      'in development': 'en_progreso' as const,
      'development': 'en_progreso' as const,
      'testing': 'en_progreso' as const,
      'review': 'en_progreso' as const,
      'en desarrollo': 'en_progreso' as const, // NUEVO del webhook
      'pendiente a qa': 'en_progreso' as const, // NUEVO del webhook
      // Estados resueltos/completados
      'resuelto': 'resuelto' as const,
      'resolved': 'resuelto' as const,
      'completed': 'resuelto' as const,
      'complete': 'resuelto' as const,
      'solucionado': 'resuelto' as const,
      'done': 'resuelto' as const,
      'finished': 'resuelto' as const,
      'ready': 'resuelto' as const,
      'deployed': 'resuelto' as const,
      'live': 'resuelto' as const,
      // Estados cerrados
      'cerrado': 'cerrado' as const,
      'closed': 'cerrado' as const,
      'finalizado': 'cerrado' as const,
      'archived': 'cerrado' as const,
      'cancelled': 'cerrado' as const,
      'canceled': 'cerrado' as const,
      'listo': 'cerrado' as const // CONFIRMADO del webhook
    };

    const normalizedStatus = statusMap[status.toLowerCase() as keyof typeof statusMap];
    return normalizedStatus || 'abierto';
  },

  /**
   * Normaliza la prioridad del ticket
   * @param {string} priority - Prioridad raw
   * @returns {string} Prioridad normalizada
   */
  normalizePriority(priority: any): 'alta' | 'media' | 'baja' {
    if (!priority || typeof priority !== 'string') return 'media';

    const priorityMap = {
      'alta': 'alta' as const,
      'high': 'alta' as const,
      'urgent': 'alta' as const,
      'crítica': 'alta' as const,
      'media': 'media' as const,
      'medium': 'media' as const,
      'normal': 'media' as const,
      'estándar': 'media' as const,
      'baja': 'baja' as const,
      'low': 'baja' as const,
      'minor': 'baja' as const,
      'info': 'baja' as const
    };

    const normalizedPriority = priorityMap[priority.toLowerCase() as keyof typeof priorityMap];
    return normalizedPriority || 'media';
  },

  /**
   * Obtiene el color CSS para el estado del ticket
   * @param {string} status - Estado del ticket
   * @returns {string} Clases CSS para el estado
   */
  getStatusBadgeClass(status: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado'): string {
    const statusStyles = {
      'abierto': 'inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full bg-red-50 text-red-700 border border-red-200 shadow-sm',
      'en_progreso': 'inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200 shadow-sm', 
      'resuelto': 'inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full bg-green-50 text-green-700 border border-green-200 shadow-sm',
      'cerrado': 'inline-flex items-center px-3 py-1.5 text-sm font-semibold rounded-full bg-slate-50 text-slate-700 border border-slate-200 shadow-sm'
    };

    return statusStyles[status] || statusStyles['abierto'];
  },

  /**
   * Obtiene el color CSS para la prioridad del ticket
   * @param {string} priority - Prioridad del ticket
   * @returns {string} Clases CSS para la prioridad
   */
  getPriorityBadgeClass(priority: 'alta' | 'media' | 'baja'): string {
    const priorityStyles = {
      'alta': 'inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg bg-red-600 text-white border border-red-700 shadow-sm',
      'media': 'inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg bg-amber-500 text-white border border-amber-600 shadow-sm',
      'baja': 'inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg bg-emerald-600 text-white border border-emerald-700 shadow-sm'
    };

    return priorityStyles[priority] || priorityStyles['media'];
  },

  /**
   * Formatea la fecha de manera legible
   * @param {string} dateString - Fecha en formato ISO
   * @returns {string} Fecha formateada
   */
  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));

      if (diffMinutes < 60) {
        return `Hace ${diffMinutes} minutos`;
      } else if (diffHours < 24) {
        return `Hace ${diffHours} horas`;
      } else if (diffDays < 7) {
        return `Hace ${diffDays} días`;
      } else {
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    } catch (error) {
      return 'Fecha no disponible';
    }
  },

  /**
   * Función de diagnóstico para rastrear problemas con assignedTo
   * @param {Array} tickets - Array de tickets para diagnosticar
   * @returns {Object} Información de diagnóstico
   */
  diagnoseAssignedToIssues(tickets: NormalizedTicket[]): DiagnosticData {
    const diagnosticData: DiagnosticData = {
      totalTickets: tickets.length,
      assignedToValues: new Map<string, number>(),
      uniqueAssignees: new Set<string>(),
      potentialIssues: [],
      ticketDetails: []
    };
    
    tickets.forEach((ticket, index) => {
      const assignedTo = ticket.assignedTo;
      const ticketDetail: TicketDetail = {
        index,
        id: ticket.id,
        assignedTo: assignedTo,
        assignedTo_type: typeof assignedTo,
        assignedTo_length: assignedTo?.length || 0,
        assignedTo_reference: assignedTo, // Para verificar si es la misma referencia
        hasAssignees: !!(ticket.assignees && ticket.assignees.length > 0),
        originalAssignees: ticket.assignees?.map(a => a.username || a.email || a.name),
        _rawAssignedTo: ticket._raw?.assignedTo
      };
      
      // Contar valores de assignedTo
      const current = diagnosticData.assignedToValues.get(assignedTo) || 0;
      diagnosticData.assignedToValues.set(assignedTo, current + 1);
      
      // Agregar a set de únicos
      diagnosticData.uniqueAssignees.add(assignedTo);
      
      // Detectar problemas potenciales
      if (!assignedTo || assignedTo === '' || assignedTo === 'undefined' || assignedTo === 'null') {
        diagnosticData.potentialIssues.push(`Ticket ${ticket.id}: assignedTo vacío o inválido`);
      }
      
      if (assignedTo === 'Elias AI' && ticket.assignees && ticket.assignees.length > 0) {
        diagnosticData.potentialIssues.push(`Ticket ${ticket.id}: Tiene assignees en ClickUp pero muestra 'Elias AI'`);
      }
      
      diagnosticData.ticketDetails.push(ticketDetail);
    });
    
    // Verificar si todos los tickets tienen el mismo assignedTo (problema principal)
    if (diagnosticData.uniqueAssignees.size === 1 && tickets.length > 1) {
      const singleValue = Array.from(diagnosticData.uniqueAssignees)[0];
      diagnosticData.potentialIssues.push(`CRÍTICO: Todos los ${tickets.length} tickets muestran el mismo assignedTo: "${singleValue}"`);
    }
    
    // Verificar referencias de objeto compartidas
    const assignedToReferences = new Set();
    tickets.forEach(ticket => {
      if (ticket.assignedTo && typeof ticket.assignedTo === 'object') {
        assignedToReferences.add(ticket.assignedTo);
      }
    });
    
    if (assignedToReferences.size === 1 && tickets.length > 1) {
      diagnosticData.potentialIssues.push('CRÍTICO: Referencias de objeto compartidas detectadas en assignedTo');
    }
    
    
    return diagnosticData;
  },

  /**
   * Diagnostica un ticket específico para detectar problemas de estado
   * @param {string} ticketId - ID del ticket a diagnosticar
   * @param {Array} tickets - Array de todos los tickets
   * @returns {Object} Información de diagnóstico detallada
   */
  diagnoseSpecificTicket(ticketId: string, tickets: NormalizedTicket[]): TicketDiagnosis {
    const ticket = tickets.find(t => t.id === ticketId);
    
    if (!ticket) {
      return {
        found: false,
        error: `Ticket ${ticketId} no encontrado en la lista de tickets`,
        potentialIssues: [],
        recommendations: []
      };
    }
    const diagnosis: TicketDiagnosis = {
      found: true,
      ticketId: ticketId,
      currentStatus: ticket.status,
      currentStatusDisplay: this.getStatusDisplayText(ticket.status),
      rawTicketData: {
        originalStatus: ticket._raw?.status,
        statusRaw: ticket._raw?.statusRaw, // NUEVO
        statusType: ticket._raw?.statusType, // NUEVO
        originalStatusObject: ticket._raw?.status?.status || ticket._raw?.status,
        progressPercent: ticket.progressPercent,
        progressBreakdown: ticket._raw?.progressBreakdown, // NUEVO
        resolved: ticket._raw?.resolved,
        assignees: ticket._raw?.assignees,
        custom_fields: ticket._raw?.custom_fields,
        location: ticket._raw?.location // NUEVO
      },
      statusAnalysis: {
        normalizedOriginal: this.normalizeStatus(ticket._raw?.statusRaw || ticket._raw?.status?.status || ticket._raw?.status),
        smartStatus: this.determineSmartStatus(
          ticket._raw?.statusRaw || ticket._raw?.status?.status || ticket._raw?.status, 
          ticket.progressPercent || 0, 
          ticket._raw?.resolved || false,
          ticket._raw?.statusType // NUEVO parámetro
        ),
        reasoning: this.getStatusReasoningLog(
          ticket._raw?.statusRaw || ticket._raw?.status?.status || ticket._raw?.status,
          ticket.progressPercent || 0,
          ticket._raw?.resolved || false
        )
      },
      potentialIssues: [],
      recommendations: []
    };
    
    // Análisis de problemas potenciales
    if (diagnosis.rawTicketData && ticket.status === 'resuelto' && diagnosis.rawTicketData.originalStatus?.toLowerCase() === 'listo') {
      diagnosis.potentialIssues.push('Estado "listo" de ClickUp está siendo mostrado como "resuelto" en lugar de "cerrado"');
      diagnosis.recommendations.push('Verificar mapeo de estado "listo" en normalizeStatus()');
    }
    
    if (diagnosis.rawTicketData && (diagnosis.rawTicketData.progressPercent || 0) >= 100 && diagnosis.rawTicketData.originalStatus?.toLowerCase() === 'abierto') {
      diagnosis.potentialIssues.push('Ticket con 100% progreso pero estado original "abierto" - posible error de webhook');
      diagnosis.recommendations.push('Verificar si el estado real en ClickUp es "listo" pero el webhook envía "abierto"');
    }
    
    if (diagnosis.rawTicketData && ticket.status !== 'cerrado' && diagnosis.rawTicketData.originalStatus?.toLowerCase() === 'listo') {
      diagnosis.potentialIssues.push('Estado "listo" de ClickUp no se está mapeando correctamente a "cerrado"');
      diagnosis.recommendations.push('Actualizar la función normalizeStatus para mapear "listo" -> "cerrado"');
    }
    
    // Verificar si hay inconsistencias de estado vs progreso
    if (diagnosis.rawTicketData && (diagnosis.rawTicketData.progressPercent || 0) < 100 && ['resuelto', 'cerrado'].includes(ticket.status)) {
      diagnosis.potentialIssues.push(`Estado '${ticket.status}' pero progreso ${diagnosis.rawTicketData.progressPercent || 0}% - inconsistencia`);
    }
    return diagnosis;
  },
  
  /**
   * Obtiene el texto de display para un estado
   * @param {string} status - Estado interno
   * @returns {string} Texto de display
   */
  getStatusDisplayText(status: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado'): string {
    const displayMap = {
      'abierto': 'Abierto',
      'en_progreso': 'En Progreso', 
      'resuelto': 'Bug Corregido',
      'cerrado': 'Cerrado'
    };
    return displayMap[status] || status;
  },
  
  /**
   * Valida si hay tickets con problemas de mapeo de estado
   * @param {Array} tickets - Array de tickets
   * @returns {Object} Reporte de validación
   */
  validateStatusMappings(tickets: NormalizedTicket[]): ValidationReport {
    const report: ValidationReport = {
      totalTickets: tickets.length,
      issuesFound: [],
      suspicious: [],
      recommendations: []
    };
    
    tickets.forEach(ticket => {
      const originalStatus = ticket._raw?.status?.status || ticket._raw?.status;
      // const normalizedStatus = this.normalizeStatus(originalStatus);
      
      // Detectar tickets "listo" que no están como "cerrado"
      if (originalStatus?.toLowerCase() === 'listo' && ticket.status !== 'cerrado') {
        report.issuesFound.push({
          ticketId: ticket.id,
          issue: 'Estado "listo" no mapeado a "cerrado"',
          originalStatus,
          currentStatus: ticket.status,
          expectedStatus: 'cerrado'
        });
      }
      
      // Detectar tickets con 100% progreso pero estado original "abierto"
      if ((ticket.progressPercent || 0) >= 100 && originalStatus?.toLowerCase() === 'abierto') {
        report.suspicious.push({
          ticketId: ticket.id,
          issue: '100% progreso pero estado original "abierto"',
          originalStatus,
          progressPercent: ticket.progressPercent,
          note: 'Posible error de webhook - verificar estado real en ClickUp'
        });
      }
      
      // Detectar inconsistencias de progreso vs estado
      if ((ticket.progressPercent || 0) < 100 && ['resuelto', 'cerrado'].includes(ticket.status)) {
        report.suspicious.push({
          ticketId: ticket.id,
          issue: 'Estado final pero progreso incompleto',
          currentStatus: ticket.status,
          progressPercent: ticket.progressPercent
        });
      }
    });
    
    // Generar recomendaciones
    if (report.issuesFound.length > 0) {
      report.recommendations.push('Verificar función normalizeStatus() para mapeo correcto de "listo" -> "cerrado"');
    }
    
    if (report.suspicious.length > 0) {
      report.recommendations.push('Revisar configuración del webhook para asegurar que envía estados actualizados de ClickUp');
      report.recommendations.push('Verificar función determineSmartStatus() para manejo correcto de estados vs progreso');
    }
    return report;
  },

  /**
   * Formatea el texto del estado para mostrar al usuario
   * @param {string} status - Estado del ticket
   * @returns {string} Texto formateado del estado
   */
  formatStatusText(status: 'abierto' | 'en_progreso' | 'resuelto' | 'cerrado'): string {
    const statusTextMap = {
      'abierto': 'Pendiente',
      'en_progreso': 'En proceso',
      'resuelto': 'Bug Corregido',
      'cerrado': 'Cerrado'
    };
    return statusTextMap[status] || status || 'Pendiente';
  },

  /**
   * Formatea el texto de la prioridad para mostrar al usuario
   * @param {string} priority - Prioridad del ticket
   * @returns {string} Texto formateado de la prioridad
   */
  formatPriorityText(priority: 'alta' | 'media' | 'baja'): string {
    const priorityTextMap = {
      'alta': 'Alta',
      'media': 'Media',
      'baja': 'Baja'
    };
    return priorityTextMap[priority] || priority || 'Media';
  },

  /**
   * Extrae la categoría del ticket desde los datos del webhook
   * @param {Object} ticket - Ticket raw del webhook
   * @returns {string} Categoría extraída o fallback
   */
  extractTicketCategory(ticket: RawTicketData): string {
    // PRIORIDAD 1: Buscar en campos personalizados (custom_fields)
    if (ticket.custom_fields && Array.isArray(ticket.custom_fields)) {
      const categoryField = ticket.custom_fields.find((field: any) => 
        field.name?.toLowerCase().includes('category') ||
        field.name?.toLowerCase().includes('categoría') ||
        field.name?.toLowerCase().includes('tipo') ||
        field.name?.toLowerCase().includes('type')
      );
      
      if (categoryField && categoryField.value) {
        return String(categoryField.value).trim();
      }
    }
    
    // PRIORIDAD 2: Buscar en location (lista de ClickUp)
    if (ticket.location?.listName) {
      return String(ticket.location.listName).trim();
    }
    
    // PRIORIDAD 3: Buscar en clickup.custom_fields
    if (ticket.clickup?.custom_fields && Array.isArray(ticket.clickup.custom_fields)) {
      const categoryField = ticket.clickup.custom_fields.find((field: any) => 
        field.name?.toLowerCase().includes('category') ||
        field.name?.toLowerCase().includes('categoría') ||
        field.name?.toLowerCase().includes('tipo') ||
        field.name?.toLowerCase().includes('type')
      );
      
      if (categoryField && categoryField.value) {
        return String(categoryField.value).trim();
      }
    }
    
    // PRIORIDAD 4: Buscar en location (folder de ClickUp)
    if (ticket.location?.folderName) {
      return String(ticket.location.folderName).trim();
    }
    
    // PRIORIDAD 5: Buscar campo category directo
    if (ticket.category) {
      return String(ticket.category).trim();
    }
    
    // PRIORIDAD 6: Fallback usando tags si hay alguno que parezca categoría
    if (ticket.tags && Array.isArray(ticket.tags) && ticket.tags.length > 0) {
      const categoryTags = ticket.tags.filter((tag: string) => 
        !tag.includes(' ') && 
        (tag.toLowerCase().includes('bug') ||
         tag.toLowerCase().includes('feature') ||
         tag.toLowerCase().includes('support') ||
         tag.toLowerCase().includes('maintenance'))
      );
      if (categoryTags.length > 0) {
        return categoryTags[0];
      }
    }
    
    // PRIORIDAD 7: Fallback basado en contenido del título
    const title = ticket.title || ticket.name || '';
    if (title.toLowerCase().includes('error') || title.toLowerCase().includes('bug')) {
      return 'Bug Report';
    }
    
    if (title.toLowerCase().includes('feature') || title.toLowerCase().includes('mejora')) {
      return 'Feature Request';
    }
    
    if (title.toLowerCase().includes('soporte') || title.toLowerCase().includes('help')) {
      return 'Soporte';
    }
    
    return 'General';
  },

  /**
   * Extrae y formatea el tiempo estimado del ticket
   * @param {Object} ticket - Ticket raw del webhook
   * @returns {Object} Tiempo estimado formateado y en milisegundos
   */
  extractTimeEstimate(ticket: RawTicketData): { formatted: string | null, milliseconds: number | null } {
    // PRIORIDAD 1: Buscar en clickup.time_estimate (ClickUp oficial)
    if (ticket.clickup?.time_estimate && typeof ticket.clickup.time_estimate === 'number') {
      const ms = ticket.clickup.time_estimate;
      return {
        formatted: this.formatDurationFromMs(ms),
        milliseconds: ms
      };
    }
    
    // PRIORIDAD 2: Buscar en campos personalizados
    if (ticket.custom_fields && Array.isArray(ticket.custom_fields)) {
      const timeField = ticket.custom_fields.find((field: any) => 
        field.name?.toLowerCase().includes('time') ||
        field.name?.toLowerCase().includes('tiempo') ||
        field.name?.toLowerCase().includes('estimate') ||
        field.name?.toLowerCase().includes('estimado') ||
        field.name?.toLowerCase().includes('duration') ||
        field.name?.toLowerCase().includes('duración')
      );
      
      if (timeField && timeField.value) {
        // Si viene como número (milisegundos)
        if (typeof timeField.value === 'number') {
          return {
            formatted: this.formatDurationFromMs(timeField.value),
            milliseconds: timeField.value
          };
        }
        // Si viene como string, intentar parsearlo
        else if (typeof timeField.value === 'string') {
          const parsed = this.parseTimeEstimateString(timeField.value);
          if (parsed) {
            return {
              formatted: timeField.value,
              milliseconds: parsed
            };
          }
        }
      }
    }
    
    // PRIORIDAD 3: Buscar en clickup.custom_fields
    if (ticket.clickup?.custom_fields && Array.isArray(ticket.clickup.custom_fields)) {
      const timeField = ticket.clickup.custom_fields.find((field: any) => 
        field.name?.toLowerCase().includes('time') ||
        field.name?.toLowerCase().includes('tiempo') ||
        field.name?.toLowerCase().includes('estimate') ||
        field.name?.toLowerCase().includes('estimado')
      );
      
      if (timeField && timeField.value) {
        if (typeof timeField.value === 'number') {
          return {
            formatted: this.formatDurationFromMs(timeField.value),
            milliseconds: timeField.value
          };
        }
        else if (typeof timeField.value === 'string') {
          const parsed = this.parseTimeEstimateString(timeField.value);
          if (parsed) {
            return {
              formatted: timeField.value,
              milliseconds: parsed
            };
          }
        }
      }
    }
    
    return { formatted: null, milliseconds: null };
  },

  /**
   * Convierte milisegundos a texto legible
   * @param {number} milliseconds - Tiempo en milisegundos
   * @returns {string|null} Tiempo formateado
   */
  formatDurationFromMs(milliseconds: number): string | null {
    if (!milliseconds || milliseconds <= 0) return null;
    
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      const remainingHours = hours % 24;
      if (remainingHours === 0) {
        return `${days} ${days === 1 ? 'día' : 'días'}`;
      } else {
        return `${days}d ${remainingHours}h`;
      }
    } else if (hours > 0) {
      const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
      if (minutes === 0) {
        return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
      } else {
        return `${hours}h ${minutes}m`;
      }
    } else {
      const minutes = Math.floor(milliseconds / (1000 * 60));
      return `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
    }
  },

  /**
   * Intenta parsear un string de tiempo estimado a milisegundos
   * @param {string} timeString - String de tiempo (ej: "2 horas", "1d 3h")
   * @returns {number|null} Tiempo en milisegundos o null
   */
  parseTimeEstimateString(timeString: string): number | null {
    if (!timeString) return null;
    
    try {
      const str = timeString.toLowerCase().trim();
      let totalMs = 0;
      
      // Buscar días
      const daysMatch = str.match(/(\d+)\s*d[íía]?[as]?/);
      if (daysMatch) {
        totalMs += parseInt(daysMatch[1]) * 24 * 60 * 60 * 1000;
      }
      
      // Buscar horas
      const hoursMatch = str.match(/(\d+)\s*h[ora]?[as]?/);
      if (hoursMatch) {
        totalMs += parseInt(hoursMatch[1]) * 60 * 60 * 1000;
      }
      
      // Buscar minutos
      const minutesMatch = str.match(/(\d+)\s*m[inuto]?[os]?/);
      if (minutesMatch) {
        totalMs += parseInt(minutesMatch[1]) * 60 * 1000;
      }
      
      return totalMs > 0 ? totalMs : null;
    } catch (error) {
      console.warn('Error parsing time estimate string:', timeString, error);
      return null;
    }
  },

  /**
   * Obtiene estadísticas resumidas de los tickets
   * @param {Array} tickets - Array de tickets
   * @returns {Object} Estadísticas de tickets
   */
  getTicketStats(tickets: NormalizedTicket[]): TicketStats {
    const stats: TicketStats = {
      total: tickets.length,
      abierto: 0,
      en_progreso: 0,
      resuelto: 0,
      cerrado: 0,
      alta: 0,
      media: 0,
      baja: 0,
      escalados: 0,
      eliasAI: 0
    };

    tickets.forEach((ticket: NormalizedTicket) => {
      // Contar por estado
      if (ticket.status === 'abierto') stats.abierto++;
      else if (ticket.status === 'en_progreso') stats.en_progreso++;
      else if (ticket.status === 'resuelto') stats.resuelto++;
      else if (ticket.status === 'cerrado') stats.cerrado++;

      // Contar por prioridad
      if (ticket.priority === 'alta') stats.alta++;
      else if (ticket.priority === 'media') stats.media++;
      else if (ticket.priority === 'baja') stats.baja++;

      // Contar por origen
      if (ticket.escalated) {
        stats.escalados++;
      } else {
        stats.eliasAI++;
      }
    });

    return stats;
  }
};

export default TicketService;

